import {
  ButtonsRowWrapper,
  ButtonsWrapper,
  ComponentWrapper,
  ExplainResultHeaderText,
  ExplainResultSubHeader,
  ExplainResultTopWrapper,
  FooterWrapper,
  LoaderWrapper,
  LoadingMessageWrapper,
  ResultContainer,
  ResultFooterText,
  StyledStartButton,
  SummarySectionsHeader,
} from './ExplainWithAiResultUI';
import { TextAreaField } from '../Form';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ILlmSummarizeSectionsInput,
  LlmSubjectKind,
  PermissionKey,
} from '../../utils/apollo/graphqlSchema';
import { LlmSummarySectionKey } from './useExplainEntityWithAiModel';
import ExplainWithAiSummarySections from './ExplainWithAiSummarySections';
import { SvgIcon } from '../Icon';
import ExplainWithAiCopyToClipboard from './ExplainWithAiCopyToClipboard';
import { KubitLogoLoader } from '../Chart/ChartLoader';
import ExplainWithAiSaveButton from './ExplainWithAiSaveButton';
import { SecondaryButton } from '../Button';
import usePermission from '../../utils/hooks/permissionHooks';
import { llmSummarizeTrackContexts } from '../../utils/analytics/events/llm/events';
import { MaybeNil } from '../../utils/interfaces';
import { useSearchParams } from 'react-router-dom';
import { UnreachableCaseError } from 'ts-essentials';
import DisabledElementsWrapper from './DisabledElementsWrapper';
import RichTextEditor from '../RichTextEditor';
import LumosIconAndWordmark from '../LumosIconAndWordmark';
import { useAppSelector } from '../../redux/hooks';
import { activeSchemaIdSelector } from '../../redux/selectors/builder';

interface IProps {
  entityLoading: boolean;
  subjectKind: LlmSubjectKind;
  subjectId: MaybeNil<string>;
  loading: boolean;
  explain: (prompt?: string) => void;
  currentResult: string | null;
  currentResultRichText: string | null;
  sections: ILlmSummarizeSectionsInput;
  toggleSection: MaybeNil<(section: LlmSummarySectionKey) => void>;
}

const entityNames: Record<LlmSubjectKind, string> = {
  [LlmSubjectKind.Analysis]: 'Report',
  [LlmSubjectKind.Dashboard]: 'Dashboard',
};

const trackActions = {
  start: 'start',
  retry: 'retry',
  save: 'save',
  copyToClipboard: 'copy to clipboard',
};

const disabledStartTooltipText = (
  subjectKind: LlmSubjectKind,
  haveEntity: boolean,
  entityLoading: boolean,
) => {
  switch (subjectKind) {
    case LlmSubjectKind.Analysis:
      return entityLoading
        ? 'This feature is disabled until the report execution is complete.'
        : !haveEntity
          ? 'This feature is disabled until a report has been executed.'
          : 'You need "Summarize With AI" permission to do this.';
    case LlmSubjectKind.Dashboard:
      return entityLoading || !haveEntity
        ? 'This feature is disabled until a Dashboard has been loaded.'
        : 'You need "Summarize With AI" permission to do this.';
    default:
      throw new UnreachableCaseError(subjectKind);
  }
};

const randomLoaderMessage = () => {
  switch (Math.floor(Math.random() * 4)) {
    case 0:
      return <div>Scanning for insights ... and maybe a few surprises!</div>;
    case 1:
      // includes a rocket emoji
      return (
        <div>Crunching numbers and hunting insights—stand by! &#x1F680;</div>
      );
    case 2:
      // includes a winking face emoji
      return <div>Analyzing like a pro, so you don’t have to. &#x1F609;</div>;
    case 3:
    default:
      // includes a magnifying glass emoji
      return <div>AI is reading between the data lines... &#x1F50D;</div>;
  }
};

const ExplainWithAiResult = ({
  subjectKind,
  subjectId,
  loading,
  explain,
  currentResult,
  currentResultRichText,
  sections,
  toggleSection,
  entityLoading,
}: IProps) => {
  // XXX temp hack to allow SEs to use the prompt
  const [params] = useSearchParams();
  const showPrompt = !!params.get('showPrompt');
  const [prompt, setPrompt] = useState('');

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const [sectionsExpanded, setSectionsExpanded] = useState(true);
  const entityName = entityNames[subjectKind];
  const [loaderMessage, setLoaderMessage] = useState(randomLoaderMessage());

  const { checkIsPermitted } = usePermission();
  const hasExplainPermission = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AiSummarize],
    schemaId: activeSchemaId,
  });

  const explainWithPrompt = useCallback(() => {
    if (!hasExplainPermission || entityLoading) {
      return;
    }
    const actualPrompt = !prompt ? undefined : prompt;
    explain(actualPrompt);
    setSectionsExpanded(false);
    setLoaderMessage(randomLoaderMessage());
  }, [explain, prompt, hasExplainPermission, entityLoading]);

  const text = currentResult ?? '';
  const trackContext = llmSummarizeTrackContexts[subjectKind];

  const explainDisabled = !hasExplainPermission || !subjectId || entityLoading;

  const disabledTooltipText = disabledStartTooltipText(
    subjectKind,
    !!subjectId,
    entityLoading,
  );

  // Automatically collapse the sections Configuration when a response is received.
  useEffect(() => {
    if (!!currentResult) {
      setSectionsExpanded(false);
    }
  }, [currentResult]);

  return (
    <ComponentWrapper>
      <ExplainResultTopWrapper>
        <LumosIconAndWordmark includeKubit={true} />
        <ExplainResultHeaderText>
          Dig deeper into your data
        </ExplainResultHeaderText>
        {!loading && !currentResult && (
          <ExplainResultSubHeader>
            Select from the list below to see tailored insights for your{' '}
            {entityName}
          </ExplainResultSubHeader>
        )}
        {!loading && (
          <DisabledElementsWrapper
            isEnabled={!explainDisabled}
            reason={disabledTooltipText}
          >
            {showPrompt && (
              <div>
                Prompt:{' '}
                <TextAreaField
                  minRows={4}
                  maxRows={12}
                  value={prompt}
                  onChange={setPrompt}
                />
              </div>
            )}
            <SummarySectionsHeader
              onClick={() => setSectionsExpanded((p) => !p)}
            >
              <div>Configuration</div>
              <div>
                <SvgIcon
                  height={16}
                  width={16}
                  name={sectionsExpanded ? 'caret-down' : 'caret-right'}
                />
              </div>
            </SummarySectionsHeader>
            {sectionsExpanded && (
              <ExplainWithAiSummarySections
                sections={sections}
                toggleSection={toggleSection}
              />
            )}
          </DisabledElementsWrapper>
        )}
      </ExplainResultTopWrapper>
      {loading ? (
        <LoaderWrapper>
          <KubitLogoLoader height={'100%'} isGradient={true}>
            {loading && (
              <LoadingMessageWrapper>{loaderMessage}</LoadingMessageWrapper>
            )}
          </KubitLogoLoader>
        </LoaderWrapper>
      ) : (
        <>
          <ResultContainer>
            {currentResultRichText && (
              <RichTextEditor disabled={true} value={currentResultRichText} />
            )}
          </ResultContainer>
          <FooterWrapper>
            <ButtonsRowWrapper>
              {!currentResult ? (
                <DisabledElementsWrapper
                  isEnabled={!explainDisabled}
                  reason={disabledTooltipText}
                >
                  <StyledStartButton
                    disabled={explainDisabled}
                    onClick={explainWithPrompt}
                    trackAction={trackActions.start}
                    trackContext={trackContext}
                  >
                    Start
                  </StyledStartButton>
                </DisabledElementsWrapper>
              ) : (
                <>
                  <ExplainWithAiCopyToClipboard
                    text={text}
                    trackContext={trackContext}
                    trackAction={trackActions.copyToClipboard}
                  />
                  <ButtonsWrapper>
                    <DisabledElementsWrapper
                      isEnabled={!explainDisabled}
                      reason={disabledTooltipText}
                    >
                      <SecondaryButton
                        isOutlined={true}
                        onClick={explainWithPrompt}
                        disabled={explainDisabled}
                        trackAction={trackActions.retry}
                        trackContext={trackContext}
                      >
                        Retry
                      </SecondaryButton>
                    </DisabledElementsWrapper>
                    {currentResultRichText && (
                      <ExplainWithAiSaveButton
                        subjectKind={subjectKind}
                        subjectId={subjectId}
                        currentResultRichText={currentResultRichText}
                        trackAction={trackActions.save}
                        trackContext={trackContext}
                      />
                    )}
                  </ButtonsWrapper>
                </>
              )}
            </ButtonsRowWrapper>
            <ResultFooterText>
              Kubit AI can make mistakes. Verify important information.
            </ResultFooterText>
          </FooterWrapper>
        </>
      )}
    </ComponentWrapper>
  );
};

export default ExplainWithAiResult;
